import Chevron from 'components/Chevron';
import Separator from 'components/Separator';
import ToggleButton from 'components/ToggleButton';
import BoxWithLightGreyFill from 'components/common/Box/BoxWithLightGreyFill';
import { EntryMethod } from 'components/enter/EntryMethod';
import EntryStack from 'components/enter/EntryStack';
import { DaymadeDrawTypePlanCode } from 'constants/Draw';
import { useEnterStore } from 'hooks/stores/useEnterStore';
import { useIsMobile } from 'hooks/ui/useIsMobile';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { extendClassName, singularOrPlural } from 'utils/common';
import {
  ASSET_PREFIX,
  colors,
  PaymentSchedule,
  WEEKS_PER_MONTH,
} from 'utils/constants';
import { getNumberOfTreesPlanted } from '../helpers/getNumberOfTreesPlanted';
import { TotalCostSection } from '../with-one-time-purchases/TotalCostSection';
import { PromoCodeEntry } from './PromoCodeEntry';

const OrderSummarySection = observer(
  ({ desktopBreakpoint = 1000 }: { desktopBreakpoint?: number }) => {
    const {
      paymentSchedule,
      days,
      dayEnabled,
      lines,
      planCode,
      entryMethod,
      boosted,
      isPaidSubscription,
    } = useEnterStore();

    const { isMobile } = useIsMobile(desktopBreakpoint);

    // This state is only used on mobile. On desktop, it's always expanded.
    const [expanded, setExpanded] = useState(!isMobile);
    const [paymentFrequencyInWeeks, setPaymentFrequencyInWeeks] = useState(
      paymentSchedule === PaymentSchedule.MONTHLY ? WEEKS_PER_MONTH : 1
    );
    const [drawsPerWeek] = useState(days.length);

    const numTreesPlantedPerWeek = getNumberOfTreesPlanted(
      entryMethod,
      dayEnabled.TUESDAY,
      dayEnabled.FRIDAY
    );

    useEffect(() => {
      setPaymentFrequencyInWeeks(
        paymentSchedule === PaymentSchedule.MONTHLY ? WEEKS_PER_MONTH : 1
      );
    }, [paymentSchedule]);

    useEffect(() => {
      setExpanded(!isMobile);
    }, [isMobile]);

    const commonContent = (
      <div className="commonContentContainer grid gap-2em">
        <div className="grid gap-1em text-centered">
          <p className="medium text-center">
            Your numbers look great!
            <br />
            Here they are once more
          </p>

          {lines.map((line, i) => (
            <EntryStack
              key={i}
              stackCount={1}
              entry={line}
              code={planCode as DaymadeDrawTypePlanCode}
              style={{
                marginTop: i === 0 ? 0 : '-15%',
                zIndex: 0 - i,
              }}
              onClick={undefined} // Consider implementing an onClick handler if necessary
            />
          ))}
        </div>

        <Separator
          // @ts-ignore
          margin={0}
        />

        <div className="flex flex-col">
          <div className="flex flex-col mx-auto">
            <p className="medium text-center">
              <img
                src={`${ASSET_PREFIX}/images/ticket.svg`}
                className="w-5 inline-block align-middle mr-2"
                alt="Ticket icon"
              />
              {singularOrPlural(
                lines.length,
                '1 entry',
                `${lines.length} entries`
              )}{' '}
              per draw
            </p>

            <p className="medium text-center">
              <img
                src={`${ASSET_PREFIX}/images/enter/calendar-icon.svg`}
                className="w-5 inline-block align-middle mr-2"
                alt="Calendar icon"
              />
              {singularOrPlural(
                drawsPerWeek * paymentFrequencyInWeeks,
                '1 draw',
                `${drawsPerWeek * paymentFrequencyInWeeks} draws`
              )}{' '}
              per week (
              {days
                .map(
                  (day) =>
                    day.charAt(0).toUpperCase() + day.slice(1).toLowerCase()
                )
                .join(' & ')}
              )
            </p>

            <p
              className={extendClassName(
                'medium text-center',
                numTreesPlantedPerWeek === 0 ? 'light-grey' : undefined
              )}
            >
              <img
                src={`${ASSET_PREFIX}/images/common/trees-planted-solid-icon.svg`}
                className="w-5 inline-block align-middle mr-2"
                alt="Trees planted icon"
              />
              {singularOrPlural(
                numTreesPlantedPerWeek * paymentFrequencyInWeeks,
                '1 tree',
                `${numTreesPlantedPerWeek * paymentFrequencyInWeeks} trees`
              )}{' '}
              planted
              {entryMethod !== EntryMethod.OneOff &&
                ` per ${paymentSchedule === PaymentSchedule.MONTHLY ? 'month' : 'week'}`}
            </p>

            {boosted && (
              <p className="medium text-center">
                <img
                  src={`${ASSET_PREFIX}/images/enter/rocket.png`}
                  className="w-5 inline-block align-middle mr-2"
                  alt="Rocket icon"
                />
                Boosted jackpot
              </p>
            )}
          </div>
        </div>

        <Separator
          // @ts-ignore
          margin={0}
        />
        <PromoCodeEntry />

        <TotalCostSection subscriptionEnabled={isPaidSubscription} />

        <p className="medium text-center bold">👋 Cancel any time 👋</p>

        {expanded && isMobile && (
          <div className="text-centered">
            <ToggleButton
              chevronReversed
              toggleHandler={() => setExpanded(false)}
              additionalClassName="fullWidth flexCentered flexRow uppercase"
              ignoreDefaultStyling
            >
              <p className="medium text-center xbold">Collapse</p>
            </ToggleButton>
          </div>
        )}

        <style>
          {`
            .commonContentContainer {
              isolation: isolate;
            }
          `}
        </style>
      </div>
    );

    return (
      <div className="orderSummary fullWidth">
        {!isMobile ? (
          <BoxWithLightGreyFill additionalClassName="flexCentered vertical-padding-2em">
            <div className="grid gap-2em vertical-padding-1em">
              <p className="large xbold noMargin flex justify-center">
                <img
                  src={`${ASSET_PREFIX}/images/enter/shopping-cart.svg`}
                  className="w-5 inline-block align-middle mr-2"
                  alt="Shopping cart icon"
                />
                Your order
              </p>

              <Separator
                // @ts-ignore
                margin={0}
              />
              {commonContent}
            </div>
          </BoxWithLightGreyFill>
        ) : (
          <div
            className="mobile1 fullWidth"
            style={{
              background: colors.footerBackground,
              width: '100%',
              padding: '0 !important',
              paddingTop: 2,
              paddingBottom: 2,
              borderRadius: '12px',
            }}
          >
            <div
              className="clickable fullWidth grid gap-1em vertical-margin-1em"
              onClick={() => setExpanded(!expanded)}
            >
              <div
                className="flexRow"
                style={{
                  justifyContent: 'space-between',
                  padding: '0 0.9em',
                  minHeight: 50,
                }}
              >
                <div>
                  <p className="large xbold" style={{ fontSize: 15 }}>
                    <img
                      src={`${ASSET_PREFIX}/images/enter/shopping-cart.svg`}
                      className="w-5 inline-block align-middle mr-2"
                      alt="Shopping cart icon"
                    />
                    Your order
                  </p>
                </div>

                <Chevron
                  direction="down"
                  reversed={expanded}
                  style={{
                    color: colors.primaryBrandGray,
                    display: 'inline-flex',
                    marginLeft: '0.7em',
                    verticalAlign: 'middle',
                    fontSize: '0.95em',
                    marginBottom: 4,
                  }}
                  inline={undefined}
                />
              </div>
            </div>
            {expanded && commonContent}
          </div>
        )}

        <style>
          {`
            .container {
              width: 100%;
              display: flex;
              justify-content: center;
            }

            :global(.orderSummary .orderSummaryIcon) {
              width: 20px;
              display: inline-block;
              vertical-align: middle;
              margin-right: 0.8em;
            }

            .mobile1 {
              padding: 0 2em;
            }

            :global(.orderSummary strong) {
              font-weight: 800;
            }

            :global(.orderSummary .editMyEntriesButton) {
              text-decoration: underline;
              cursor: pointer;
            }

            @media (max-width: 400px) {
              .mobile1 {
                padding: 0 0.8em;
                margin: 0.5em 0;
              }
            }

            :global(.desktopContent) {
              width: 100%;
              max-width: 480px;
              margin: 2em 0;
              border-radius: 12px;
              text-align: center;
              display: none;
            }

            @media (min-width: ${desktopBreakpoint.toString()}px) {
              .mobile1 {
                display: none;
              }
              :global(.desktopContent) {
                display: flex;
              }
            }
          `}
        </style>
      </div>
    );
  }
);

export default OrderSummarySection;
